import React, { useEffect, useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'

import ResponsiveFont from '../ResponsiveFont/ResponsiveFont'
import IconFile from './aex_train.jsx'

const smallScreen = window.innerWidth <= 768
const largeScreen = window.innerWidth >= 1440

let imageShadowSize = null
let textShadowSize = null
let scaleUp = null
let slideUp = null
let slideDown = null

if (smallScreen) {
    imageShadowSize = '10px'
    textShadowSize = '3px'
    scaleUp = '1.08'
    slideUp = '6vh'
    slideDown = '-6vh'
} else if (largeScreen) {
    imageShadowSize = '15px'
    textShadowSize = '8px'
    scaleUp = '1.04'
    slideUp = '6vh'
    slideDown = '-6vh'
} else {
    imageShadowSize = '12px'
    textShadowSize = '4px'
    scaleUp = '1.06'
    slideUp = '7vh'
    slideDown = '-6vh'
}

const AnimationSettings = {
    dropShadowImage: `drop-shadow(0px -1px ${imageShadowSize} #d3d3d3)`,
    dropShadowText: `drop-shadow(0px -1px ${textShadowSize} #d3d3d3)`,
    dropShadowTransparent: 'drop-shadow(0px 0px 0px transparent)',
    scaleUp: `scale(${scaleUp})`,
    scaleDown: 'scale(1.0)',
    slideUp: `translateY(${slideUp})`,
    slideDown: `translateY(${slideDown})`,
    fadeInOut: '0.5',
    fadeIn: '0',
    onChange: '1.25s ease-in-out normal',
    onDeparture: '2s ease-in-out infinite',
    onFirstChild: '5s ease-in-out infinite',
}

// //Hook
const usePreviousValue = (value: any) => {
    const ref = useRef<any>(null)
    useEffect(() => {
        ref.current = value
    })
    return ref.current
}

// A PureComponent
const DepartureCount = React.memo<{
    count: number
    position: number
    isLast: boolean
}>(({ count, position, isLast }) => {
    const [value, setValue] = useState<number | undefined>(undefined)
    const [newValue, setNewValue] = useState<number | 'Now' | undefined>(undefined)
    const [onChangeAnimation, setOnChangeAnimation] = useState(false)
    const [onDepartureAnimation, setOnDepartureAnimation] = useState(false)
    const previousValue = usePreviousValue(value)

    useEffect(() => {
        setValue(count)

        if (previousValue !== value) {
            setOnChangeAnimation(true)

            if (value && value > 0) {
                //Half second delay on minute update to sync better with animation
                setTimeout(() => {
                    //Add animation class
                    setNewValue(value)
                    //Remove departure animation
                    setOnDepartureAnimation(false)
                }, 300)
                //Remove animation class after 3 seconds
                setTimeout(() => {
                    setOnChangeAnimation(false)
                }, 3000)
            } else {
                setTimeout(() => {
                    //Set value to 'Now' instead of '0 min'
                    setNewValue('Now')
                    //Add infinite animation on departure
                    setOnDepartureAnimation(true)
                    //Remove on change animation
                    setOnChangeAnimation(false)
                }, 300)
            }
        }
    }, [count, value, previousValue])

    if (count >= 60) {
        return null // Renders something else if more than 59 minutes to next train
    }

    //Classes based on animation values
    const blockClasses = `
    ${onChangeAnimation ? 'singlePulse' : ''}
    ${onDepartureAnimation ? 'infinitePulse' : ''}
    ${isLast ? 'isLast' : ''}
    `

    const textClasses = `
    ${onChangeAnimation && !isLast ? 'fadeInOut' : ''}
    ${onChangeAnimation && isLast ? 'fadeIn' : ''}
    ${onDepartureAnimation && !isLast ? 'fadeInUp' : ''}
    ${onDepartureAnimation && isLast ? 'fadeInDown' : ''}
    `

    return (
        <Time data-pos={position} className={blockClasses}>
            <Container>
                <Icon className="icon">
                    <IconFile />
                </Icon>
            </Container>
            <Container>
                <Text className={`text ${textClasses}`}>
                    <ResponsiveFont ratio={6}>
                        <Value className="value">{newValue}</Value>
                        <Unit className="unit">{newValue && newValue !== 'Now' ? 'min' : ''}</Unit>
                    </ResponsiveFont>
                </Text>
            </Container>
            {isLast && (
                <Container className="fadeInUp">
                    <ResponsiveFont ratio={18}>
                        <Value className="subtitle">
                            Last express <br /> for today!
                        </Value>
                    </ResponsiveFont>
                </Container>
            )}
        </Time>
    )
})

export default DepartureCount

//Glow animation keyframe
const imageGlow = keyframes`
    0% {
        transform: ${AnimationSettings.scaleDown};
        filter: ${AnimationSettings.dropShadowTransparent};
    }

    50% {
        transform: ${AnimationSettings.scaleUp};
        filter: ${AnimationSettings.dropShadowImage};
    }

    100% {
        transform: ${AnimationSettings.scaleDown};
        filter: ${AnimationSettings.dropShadowTransparent};
    }
`

const fadeInOut = keyframes`
    0% {
        opacity: 1;
        
    }

    50% {
        opacity: ${AnimationSettings.fadeInOut};
    }

    100% {
        opacity: 1;
    }
`

const fadeIn = keyframes`
    0% {
        opacity: ${AnimationSettings.fadeIn};
        
    }

    100% {
        opacity: 1;
    }
`

const fadeInUp = keyframes`
    0% {
        opacity: 0;
        transform: ${AnimationSettings.slideUp};
        
    }

    100% {
        opacity: 1;
        transform: translateY(0);
    }
`

const fadeInDown = keyframes`
    0% {
        opacity: 0;
        transform: ${AnimationSettings.slideDown};
        
    }

    100% {
        opacity: 1;
        transform: translateY(0);
    }
`

const Icon = styled.div`
    svg {
        height: 100%;
        width: 100%;
    }
`

const Text = styled.div`
    span {
        display: inline-flex;
        align-items: baseline;
    }

    &.fadeInOut {
        animation: ${fadeInOut} 0.8s ease-out normal;
    }

    &.fadeIn {
        animation: ${fadeIn} 1s ease-in normal;
    }

    &.fadeInUp {
        animation: ${fadeInUp} 0.5s ease-in normal;
    }

    &.fadeInDown {
        animation: ${fadeInDown} 0.5s ease-in normal;
    }
`
const Value = styled.span`
    display: block;
    font-family: TransferSans, 'Transfer Sans', sans-serif;
    line-height: 1;
`

const Unit = styled.span`
    display: block;
    font-weight: bold;
`

const Container = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
`

const Time = styled.div`
    display: flex;
    flex-direction: column;
    width: 23%;
    transition: all 1s ease-in-out;

    @media screen and (min-width: 2000px) and (max-height: 1024px) { 
        width: 15%;
     }

    @media (orientation: portrait) {
        position: relative;
        flex-direction: row;
        align-items: baseline;
        width: 100%;
        margin-bottom: 3vh;
        margin-top: 1vh;
        margin-left: 15vw;

        .text {
            span {
                line-height: 0;
            }

            .value {
                margin-left: 4vw;
            }
        }
    }

    @media (orientation: landscape) {
        margin-top: 5vh;
    }

    &:first-child {
        svg {
            animation: ${imageGlow} ${AnimationSettings.onFirstChild};
        }

        &.infinitePulse svg {
            animation: ${imageGlow} ${AnimationSettings.onDeparture};
            animation-delay: 0.3s;
        }

        .value {
            font-size: 100%;
        }

        .unit {
            font-size: 20%;
        }

        @media (orientation: portrait) {
            .icon {
                width: calc(8vh + 8vw);
            }

            .fadeInUp .value,
            .fadeInDown .value  {
                font-size: 60%;
                font-kerning: none;
            }
        }

        @media (orientation: landscape) {
            margin-right: 7vw;

            .icon {
                width: 60%;
            }

            .value {
                margin-left: -0.5vw;
            }

            .text {
                margin-top: -3vh;

                &.fadeInUp {
                    margin-top: 0;

                    .value {
                        font-size: 55%;
                    }
                }
            }
        }

        @media screen and (min-width: 2000px) and (max-height: 1024px) { 
            .text {
                .value {
                    font-size: 70%;
                }

                .unit {
                    font-size: 15%;
                }

                &.fadeInUp .value {
                    font-size: 32%;
                }
            }
            
         }

        &.isLast {
            flex-direction: row;
            flex-wrap: wrap;
            align-items: baseline;

            .fadeInDown .value {
                font-size: 50%;
            }

            .value {
                line-height: 0;
            }

            .subtitle {
                margin-top: 2vh;
            }

            @media(orientation: portrait) {
                width: 80vw;

                .icon {
                    width: 17vw;
                }

                .value {
                    margin-left 5vw;
                }

                .unit {
                    font-size: 20%;
                }

                .text {
                    min-width: 50vw;
                }
            }

            @media(orientation: landscape) {
                margin-top: 22vh;
                width: 37vw;

                .icon {
                    width: 10vw;
                }

                .value {
                    margin-left 2vw;
                    font-size: 80%;
                }

                .unit {
                    font-size: 15%;
                }
            }
        }
    }

    &:nth-child(2) {
        opacity: 0.7;

        .value {
            font-size: 70%;
        }

        .unit {
            font-size: 15%;
        }

        @media (orientation: portrait) {

            .icon {
                width: calc(6.4vh + 6.4vw);
            }
        }

        @media (orientation: landscape) {
            margin-right: 2.5vw;

            .icon {
                width: 48%;
            }
            
            .text {
                margin-top: -1vh;
            }
        }

        @media screen and (min-width: 2000px) and (max-height: 1024px) { 
            .value {
                font-size: 51%;
            }

            .unit {
                font-size: 12%;
            }
         }
    }

    &:nth-child(3) {
        opacity: 0.5;

        .value {
            font-size: 50%;
        }

        .unit {
            font-size: 12%;
        }

        @media (orientation: portrait) {
            .icon {
                width: calc(5.1vh + 5.1vw);
            }

        }

        @media (orientation: landscape) {
            .icon {
                width: 38%;
            }
        }

        @media screen and (min-width: 2000px) and (max-height: 1024px) { 
            .value {
                font-size: 32%;
            }

            .unit {
                font-size: 8%;
            }
         }
    }
`
